import type {
  AccountTypeRebateItem,
  AccountTypeRebateListParams,
  AdAccountRebateItem,
  AdAccountRebateListParams,
  CreateAccountTypeRebateParams,
  CreateAdAccountRebateParams,
  CustomerRebateAccountItem,
  CustomerRebateAccountListParams,
  CustomerRebateItem,
  CustomerRebateListParams,
  CustomerRebateMinDateParams,
  CustomerRebateUpsertParams,
  QueryUnconfiguredAccountsParams,
  UnconfiguredAccountItem,
  UpdateAccountTypeRebateParams,
  UpdateAdAccountRebateParams,
} from './type/rebate'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { dayjs } from '#/utils'

export const rebateApi = defineApi('operation', ({ prefix }) => {
  // ========== 账户类型返点相关接口 ==========

  // POST /gobestads/v1.0/operation/adsAccountTypeRebate/query
  function getAccountTypeRebateListParams(
    params: PagingRequest<AccountTypeRebateListParams>,
  ) {
    return {
      agency: params.condition?.agency,
      mediaPlatform: params.condition?.mediaPlatform,
      accountType: params.condition?.accountType,
      status: params.condition?.status,
    }
  }

  function getAccountTypeRebateList(
    params: PagingRequest<AccountTypeRebateListParams>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: getAccountTypeRebateListParams(params),
    }
    return requestClient
      .post(prefix('adsAccountTypeRebate/query'), requestParams)
      .then((res) => {
        return {
          items:
            res?.list?.map((item: AccountTypeRebateItem) => ({
              id: item.id,
              agency: item.agency,
              mediaPlatform: item.mediaPlatform,
              accountType: item.accountType,
              rebate: item.rebate,
              status: item.status,
              createdTime: item.createdTime,
              updatedTime: item.updatedTime,
              createdBy: item.createdBy,
              updatedBy: item.updatedBy,
            })) || [],
          total: res?.total || 0,
        }
      })
  }

  // POST /gobestads/v1.0/operation/adsAccountTypeRebate/create
  function createAccountTypeRebate(params: CreateAccountTypeRebateParams) {
    return requestClient.post(prefix('adsAccountTypeRebate/create'), {
      agency: params.agency,
      mediaPlatform: params.mediaPlatform,
      accountType: params.accountType,
      rebate: Number(params.rebate),
      status: Number(params.status),
    })
  }

  // POST /gobestads/v1.0/operation/adsAccountTypeRebate/update
  function updateAccountTypeRebate(params: UpdateAccountTypeRebateParams) {
    return requestClient.post(prefix('adsAccountTypeRebate/update'), {
      id: params.id,
      rebate: Number(params.rebate),
      status: Number(params.status),
    })
  }

  // ========== 广告账户返点相关接口 ==========

  // POST /gobestads/operation/adsAccountRebate/query
  function getAdAccountRebateListParams(
    params: PagingRequest<AdAccountRebateListParams>,
  ) {
    return {
      agency: params.condition?.agency,
      mediaPlatform: params.condition?.mediaPlatform,
      accountType: params.condition?.accountType,
      accountId: params.condition?.accountId,
      accountName: params.condition?.accountName,
      status: params.condition?.status,
    }
  }

  function getAdAccountRebateList(
    params: PagingRequest<AdAccountRebateListParams>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: getAdAccountRebateListParams(params),
    }
    return requestClient
      .post(prefix('adsAccountRebate/query'), requestParams)
      .then((res) => {
        return {
          items:
            res?.list?.map((item: AdAccountRebateItem) => ({
              id: item.id,
              accountId: item.accountId,
              accountName: item.accountName,
              rebate: item.rebate,
              status: item.status,
              updatedTime: item.updatedTime,
              updatedBy: item.updatedBy,
              agency: item.agency,
              accountType: item.accountType,
              accountTypeRebate: item.accountTypeRebate,
              mediaPlatform: item.mediaPlatform,
            })) || [],
          total: res?.total || 0,
        }
      })
  }

  // POST /gobestads/operation/adsAccountRebate/create
  function createAdAccountRebate(params: CreateAdAccountRebateParams) {
    return requestClient.post(prefix('adsAccountRebate/create'), {
      accountIds: params.accountIds,
      rebate: Number(params.rebate),
      status: Number(params.status),
    })
  }

  // POST /gobestads/operation/adsAccountRebate/update
  function updateAdAccountRebate(params: UpdateAdAccountRebateParams) {
    return requestClient.post(prefix('adsAccountRebate/update'), {
      ids: params.ids,
      rebate: params.rebate ? Number(params.rebate) : undefined,
      status: params.status ? Number(params.status) : undefined,
    })
  }

  // POST /gobestads/operation/adsAccountRebate/queryUnconfiguredAccounts
  function getUnconfiguredAccounts(
    params: PagingRequest<QueryUnconfiguredAccountsParams>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: {
        keyword: params.condition?.keyword,
        mediaPlatform: params.condition?.mediaPlatform,
      },
    }
    return requestClient
      .post(prefix('adsAccountRebate/queryUnconfiguredAccounts'), requestParams)
      .then((res) => {
        return {
          items:
            res?.list?.map((item: UnconfiguredAccountItem) => ({
              id: item.id,
              mediaPlatform: item.mediaPlatform,
              accountId: item.accountId,
              accountName: item.accountName,
            })) || [],
          total: res?.total || 0,
        }
      })
  }

  // ========== 客户返点相关接口 ==========

  // POST /gobestads/operation/adsCustomerRebate/query
  function getCustomerRebateListParams(
    params: PagingRequest<CustomerRebateListParams>,
  ) {
    const updatedTimeRange =
      params.condition?.updatedTimeRange &&
      Array.isArray(params.condition.updatedTimeRange) &&
      params.condition.updatedTimeRange.length === 2
        ? params.condition.updatedTimeRange.map((date: string) =>
            dayjs(date).format('YYYY-MM-DD'),
          )
        : undefined

    return {
      updatedTimeRange,
      mediaPlatform: params.condition?.mediaPlatform,
      accountId: params.condition?.accountId,
      accountName: params.condition?.accountName,
      customerId: params.condition?.customerId,
      customerName: params.condition?.customerName,
      updatedBy: params.condition?.updatedBy,
    }
  }

  function getCustomerRebateList(
    params: PagingRequest<CustomerRebateListParams>,
  ) {
    return requestClient
      .post(prefix('adsCustomerRebate/query'), {
        condition: getCustomerRebateListParams(params),
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => {
        return {
          items:
            res?.list?.map((item: CustomerRebateItem) => ({
              id: item.id,
              mediaPlatform: item.mediaPlatform,
              accountId: item.accountId,
              accountName: item.accountName,
              rebateStartDate: item.rebateStartDate,
              rebateEndDate: item.rebateEndDate,
              customerRebate: item.customerRebate,
              customerId: item.customerId,
              customerName: item.customerName,
              updatedTime: item.updatedTime,
              updatedBy: item.updatedBy,
            })) || [],
          total: res?.total || 0,
        }
      })
  }

  // POST /gobestads/operation/adsCustomerRebate/upsert
  function upsertCustomerRebate(params: CustomerRebateUpsertParams) {
    return requestClient.post(prefix('adsCustomerRebate/upsert'), {
      id: params.id,
      mediaPlatform: params.mediaPlatform,
      accountId: params.accountId,
      accountName: params.accountName,
      rebateStartDate: dayjs(params.rebateStartDate).format('YYYY-MM-DD'),
      customerRebate: Number(params.customerRebate),
      customerId: params.customerId,
    })
  }

  // POST /gobestads/operation/adsCustomerRebate/accountList
  function getCustomerRebateAccountList(
    params: PagingRequest<CustomerRebateAccountListParams>,
  ) {
    return requestClient
      .post(prefix('adsCustomerRebate/accountList'), {
        condition: {
          mediaPlatform: params.condition?.mediaPlatform,
          account: params.condition?.account,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => {
        return {
          items:
            res?.list?.map((item: CustomerRebateAccountItem) => ({
              id: item.id,
              mediaPlatform: item.mediaPlatform,
              accountId: item.accountId,
              accountName: item.accountName,
              agency: item.agency,
              canSelect: item.canSelect,
            })) || [],
          total: res?.total || 0,
        }
      })
  }

  // POST /gobestads/operation/adsCustomerRebate/minDate
  function getCustomerRebateMinDate(params: CustomerRebateMinDateParams) {
    return requestClient
      .post(prefix('adsCustomerRebate/minDate'), {
        accountId: params.accountId,
        mediaPlatform: params.mediaPlatform,
      })
      .then((res) => {
        return {
          minDate: res?.minDate,
        }
      })
  }

  return {
    // 账户类型返点
    getAccountTypeRebateListParams,
    getAccountTypeRebateList,
    createAccountTypeRebate,
    updateAccountTypeRebate,
    // 广告账户返点
    getAdAccountRebateListParams,
    getAdAccountRebateList,
    createAdAccountRebate,
    updateAdAccountRebate,
    getUnconfiguredAccounts,
    // 客户返点
    getCustomerRebateListParams,
    getCustomerRebateList,
    upsertCustomerRebate,
    getCustomerRebateAccountList,
    getCustomerRebateMinDate,
  }
})
